<template>
  <div>
    <div class="filtro-status">
      <label for="franquias-filtro-status">{{ t('Status') }}:</label>
      <select
        id="franquias-filtro-status"
        data-testid="franquias-filtro-status"
        v-model="filtroStatus">
        <option value="ativas">{{ t('Ativas') }}</option>
        <option value="inativas">{{ t('Inativas') }}</option>
        <option value="todas">{{ t('Todas') }}</option>
      </select>
    </div>

    <data-table
      :key="tabelaKey"
      ref="datatableRef"
      data-testid="franquias-table"
      :config="datatableConfig"
      :data="franquiasVisiveis"
      :refreshButton="refreshButtonConfig"
      @refresh="loadData"
      @row-click="handleRowClick"
      @edit-click="handleEditClick"
      @button-click="handleButtonClick">
    </data-table>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { useFranquias } from '../../composables/useFranquias';
import { useLocalidades } from '../../composables/useLocalidades';
import { eventBus } from '../../eventBus';
import { permissionsService } from '../../services/permissionsService';
import { useConfirmAction } from '../../composables/useConfirmAction';

type Id = number | string;

interface DataTableInstance {
  loadData: () => void;
}

interface FranquiaRow {
  ID?: Id;
  id?: Id;
  Nome?: string;
  Email?: string;
  CNPJ?: string;
  Pais?: string;
  // Chamado 2026-0023. Ausente = ativa: num ambiente onde a coluna ainda não foi
  // criada, o backend devolve true, e uma franquia sem status nunca some da tela.
  Ativo?: boolean;
  [key: string]: unknown;
}

/** Chamado 2026-0023: o filtro de status da tela. */
type FiltroStatus = 'ativas' | 'inativas' | 'todas';

interface FetchFranquiasResponse {
  franquias?: FranquiaRow[] | Record<string, { Franquia?: FranquiaRow } & FranquiaRow>;
  paises?: number;
  cidades?: number;
  estados?: number;
  idiomas?: number;
}

interface ButtonClickPayload {
  action?: string;
  data?: FranquiaRow;
}

interface Props {
  datatablesLanguageUrl?: string;
}

interface PaisLookupItem {
  Name?: string;
  Code?: string;
}

const { t } = useI18n();
const { fetchFranquias, setAtivo } = useFranquias();
const { confirmAction } = useConfirmAction();
const { fetchPaises } = useLocalidades();

withDefaults(defineProps<Props>(), {
  datatablesLanguageUrl: '',
});

// Component refs
const datatableRef = ref<DataTableInstance | null>(null);

// Reactive data
const franquiasData = ref<FranquiaRow[]>([]);
// Ativas por padrão: quem abre /franquias quer ver a rede que opera hoje. As
// inativas continuam a um clique, nunca escondidas de vez.
const filtroStatus = ref<FiltroStatus>('ativas');
// Remonta a tabela quando um interruptor precisa voltar ao lugar (ver pedirMudancaDeStatus).
const tabelaKey = ref<number>(0);
const isLoading = ref<boolean>(false);
const rawResponseData = ref<{ Success: boolean; Dados: FetchFranquiasResponse } | null>(null);

// Emit events
const emit = defineEmits<{
  'edit': [franquiaId: Id];
}>();

const tableColumns = computed(() => {
  // Só o Full muda o status, como o endpoint.
  const podeMudarStatus = permissionsService.isUserInGroups(['Full']);
  const columns: Record<string, unknown>[] = [
    {
      data: 'ID',
      title: t('ID'),
      width: 80,
      minWidth: 60,
      maxWidth: 100,
      resizable: true,
      sortable: true,
      renderType: 'clickable_id',
    },
    {
      data: 'Nome',
      title: t('Franquia'),
      width: 250,
      minWidth: 200,
      resizable: true,
      sortable: true,
    },
    {
      data: 'Email',
      title: t('Email'),
      width: 200,
      minWidth: 150,
      resizable: true,
      sortable: true,
    },
    {
      data: 'CNPJ',
      title: t('Documento'),
      width: 150,
      minWidth: 120,
      resizable: true,
      sortable: true,
    },
    {
      data: 'Pais',
      title: t('País'),
      width: 150,
      minWidth: 120,
      resizable: true,
      sortable: true,
    },
    // Chamado 2026-0054: para o Full a coluna é o próprio interruptor — ligado = ativa. O
    // status só mudava pela API. Desligar pede confirmação; ligar, não. Os demais grupos
    // continuam vendo o texto. Ausente = ativa (ver FranquiaRow): a carga normaliza o campo.
    podeMudarStatus
      ? {
          data: 'Ativo',
          title: t('Ativa'),
          width: 110,
          minWidth: 90,
          resizable: true,
          sortable: true,
          renderType: 'toggle',
          toggleCallback: (rowData: FranquiaRow, newValue: boolean): void => pedirMudancaDeStatus(rowData, newValue),
        }
      : {
          data: 'Ativo',
          title: t('Status'),
          width: 120,
          minWidth: 100,
          resizable: true,
          sortable: true,
          cellRenderer: ({ data }: { data: FranquiaRow }): string =>
            data?.Ativo === false ? t('Inativa') : t('Ativa'),
        },
  ];

  // Adicionar coluna de ações apenas para usuários com permissão
  const hasPermission = permissionsService.isUserInGroups(['Full', 'Franqueado', 'Gerente']);
  if (hasPermission) {
    columns.push({
      data: null,
      title: t('Ações'),
      width: 120,
      resizable: false,
      sortable: false,
      renderType: 'action_buttons',
      buttons: [
        {
          text: t('Metas'),
          icon: 'pi pi-chart-bar',
          class: 'p-button-secondary p-button-sm',
          action: 'configs-avancadas',
          dataTestId: (rowData: FranquiaRow): string => `btn-configs-avancadas-${rowData.ID || rowData.id}`,
        },
      ],
    });
  }

  return columns;
});

/**
 * As linhas que a tabela mostra, já filtradas por status (chamado 2026-0023).
 *
 * O filtro é de TELA e nada mais: não toca no filtro global, não muda o select de
 * franquias e não esconde nada de quem pede a franquia pelo id.
 */
const franquiasVisiveis = computed<FranquiaRow[]>(() => {
  if (filtroStatus.value === 'todas') {
    return franquiasData.value;
  }
  const querAtivas = filtroStatus.value === 'ativas';

  return franquiasData.value.filter((row) => (row.Ativo !== false) === querAtivas);
});

const datatableConfig = computed(() => {
  return {
    tableId: 'tabelaFranquias',
    title: t('Franquias'),
    filename: `franquias_${new Date().toISOString().split('T')[0]}`,
    columns: tableColumns.value,
    pageLength: 25,
    height: '500px',
    footer: false,
    rowSelection: 'single',
    onEditClick: handleEditClick,
  };
});

const refreshButtonConfig = computed(() => ({
  show: true,
  text: t('Atualizar'),
}));

/**
 * Resolve a franquia's raw `Pais` (name pre-migration, ISO code
 * post-migration — S1b) to its display Name via the locations-service
 * registry. Falls back to the raw value when it matches neither (e.g. the
 * registry is unreachable) — the column still shows SOMETHING useful.
 */
const resolveCountryDisplayName = (raw: string | undefined, paises: PaisLookupItem[]): string => {
  if (!raw) {
    return '';
  }
  const byCode = paises.find((p) => (p.Code || '').toUpperCase() === raw.toUpperCase());
  if (byCode?.Name) {
    return byCode.Name;
  }
  const byName = paises.find((p) => (p.Name || '').toLowerCase() === raw.toLowerCase());
  if (byName?.Name) {
    return byName.Name;
  }
  return raw;
};

// Data loading
const loadData = async (): Promise<void> => {
  try {
    isLoading.value = true;

    const [dados, paises] = await Promise.all([
      fetchFranquias() as Promise<FetchFranquiasResponse | null>,
      fetchPaises().catch(() => []) as Promise<PaisLookupItem[]>,
    ]);

    // Store raw response for stats
    rawResponseData.value = { Success: true, Dados: dados ?? {} };

    // Process response - extract franchises from nested structure
    // Response structure from composable: { franquias, paises, cidades, ... }
    if (dados) {
      // Extract franchises array from object
      let franquiasArray: FranquiaRow[] = [];
      if (dados.franquias) {
        const raw: Array<{ Franquia?: FranquiaRow } & FranquiaRow> = Array.isArray(dados.franquias)
          ? dados.franquias as Array<{ Franquia?: FranquiaRow } & FranquiaRow>
          : Object.values(dados.franquias);
        franquiasArray = raw.map((item) => item.Franquia || item);
      }

      franquiasArray = franquiasArray.map((row) => ({
        ...row,
        Pais: resolveCountryDisplayName(row.Pais, paises),
        // Ausente = ativa: o interruptor lê o campo cru, e sem isto mostraria "desligado".
        Ativo: row.Ativo !== false,
      }));

      franquiasData.value = franquiasArray;

      // Emit statistics
      //
      // Chamado 2026-0023: o KPI "Total" conta so as ATIVAS. O numero que a
      // franqueadora olha para dizer "temos N franquias" e o da rede que opera hoje;
      // somar as desativadas o inflava. As inativas continuam na tabela, a um clique
      // no filtro — o que mudou e o que o numero AFIRMA.
      const stats = {
        total: franquiasArray.filter((row) => row.Ativo !== false).length,
        paises: dados.paises || 0,
        cidades: dados.cidades || 0,
        estados: dados.estados || 0,
        idiomas: dados.idiomas || 0,
      };

      eventBus.emit('franquias.stats-updated', stats);
    } else {
      franquiasData.value = [];
    }
  } catch {
    eventBus.emit('globalMessage.error', t('Erro ao carregar franquias'));
    franquiasData.value = [];
  } finally {
    isLoading.value = false;
  }
};

// Event handlers

const handleRowClick = (_rowData: unknown): void => {
  // Row click handler
};

const handleEditClick = (franquiaId?: Id): void => {
  if (franquiaId) {
    emit('edit', franquiaId);
  }
};

/**
 * Liga ou desliga a franquia e recarrega a lista — o filtro de status e os KPIs (que contam só
 * as ativas) saem da própria carga.
 */
const mudarStatus = async (row: FranquiaRow, ativo: boolean): Promise<void> => {
  const franquiaId = Number(row.ID || row.id);
  if (!franquiaId) return;
  try {
    await setAtivo(franquiaId, ativo);
    eventBus.emit('globalMessage.success', ativo ? t('Franquia reativada') : t('Franquia inativada'));
    await loadData();
  } catch (error) {
    const message = error instanceof Error ? error.message : '';
    eventBus.emit('globalMessage.error', t('Erro ao alterar o status da franquia: ') + message);
    // Nada foi gravado: o interruptor volta ao que o dado diz.
    tabelaKey.value += 1;
  }
};

/**
 * O interruptor da coluna "Ativa" mudou. Desligar é a ação que tira a franquia da gestão:
 * pede confirmação, e recusar recarrega a lista para o interruptor voltar ao lugar.
 */
const pedirMudancaDeStatus = (row: FranquiaRow, ativo: boolean): void => {
  if (ativo) {
    mudarStatus(row, true);
    return;
  }
  confirmAction({
    header: t('Inativar franquia'),
    message: `${t('Inativar a franquia')} ${row.Nome || ''}? ${t('Ela sai da lista de ativas e da contagem da tela. O acesso dos usuários e o histórico não mudam.')}`,
    acceptLabel: t('Inativar'),
    rejectLabel: t('Cancelar'),
    onAccept: () => { mudarStatus(row, false); },
    // O interruptor já virou na tela no clique, e o dado não mudou: recarregar a lista não o
    // traz de volta (o valor continua "ativa", nada re-renderiza). Remontar a tabela traz.
    onReject: () => { tabelaKey.value += 1; },
  });
};

const handleButtonClick = (buttonData?: ButtonClickPayload): void => {
  if (buttonData?.action === 'configs-avancadas') {
    const franquiaId = buttonData.data?.ID || buttonData.data?.id;
    if (franquiaId) {
      eventBus.emit('franquias.configs.avancadas', { franquiaId });
    }
  }
};

/**
 * Refresh table data
 */
const refreshData = (): void => {
  loadData();
};

// Listen to refresh events
const handleRefresh = (): void => {
  refreshData();
};

onMounted(() => {
  eventBus.on('franquias.refresh', handleRefresh);

  // Load data on mount
  loadData();
});

onBeforeUnmount(() => {
  eventBus.off('franquias.refresh', handleRefresh);
});

// Expose for parent
defineExpose({
  refreshData,
});
</script>

<style scoped>
.filtro-status {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}

/* Styles are handled by data-table component */
</style>
